import { Button, ButtonVariants, Dialog, DialogFooterWrapper, Text, useDisclosure, VStack } from '@talos/kyoko';
import { useCallback, useMemo, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { useAppStateDispatch, useAppStateSelector } from '../../../providers/AppStateProvider';
import { preTradeApi, useGetPreTradeDataQuery } from '../redux/preTradeApi';
import { selectActiveSessionId, setActiveSession } from '../redux/preTradeSlice';

export type SessionAction = 'add-to-existing' | 'create-new' | 'cancel';

interface ConfirmationDialogProps {
  isOpen: boolean;
  currentSymbol: string;
  newSymbol: string;
  onAction: (action: SessionAction) => void;
}

function SessionConfirmationDialog({ isOpen, currentSymbol, newSymbol, onAction }: ConfirmationDialogProps) {
  const customActions = useMemo(
    () => (
      <DialogFooterWrapper>
        <Button variant={ButtonVariants.Default} onClick={() => onAction('create-new')}>
          Create New Session
        </Button>
        <Button variant={ButtonVariants.Primary} onClick={() => onAction('add-to-existing')}>
          Add to Existing Session
        </Button>
      </DialogFooterWrapper>
    ),
    [onAction]
  );

  return (
    <Dialog
      isOpen={isOpen}
      close={() => onAction('cancel')}
      title="Symbol Changed"
      showClose
      showConfirm={false}
      showCancel={false}
      customActions={customActions}
      width={500}
    >
      <VStack gap="spacing8" alignItems="stretch">
        <Text color="colorTextDefault">
          The symbol has changed from <strong>{currentSymbol}</strong> to <strong>{newSymbol}</strong>.
        </Text>
        <Text color="colorTextSubtle">
          Would you like to add this scenario to the existing session or create a new session?
        </Text>
      </VStack>
    </Dialog>
  );
}

export interface SessionConfirmationResult {
  sessionId: string;
  action: SessionAction;
}

interface PendingConfirmationState {
  // Target session currently under confirmation.
  sessionId: string;
  // Deduplicated symbols from the target session, shown in modal copy.
  currentSymbols: string[];
  // Requested symbol for the new scenario.
  newSymbol: string;
  // Promise resolver captured when resolveSessionID needs user input.
  resolve: (result: SessionConfirmationResult) => void;
}

/**
 * Encapsulates all session-selection behavior for pre-trade submission.
 *
 * Consumer contract:
 * - call `resolveSessionID(newSymbol)` and await a resolved `{ sessionId, action }`
 * - render `ConfirmationDialog` so the hook can ask for user input when needed
 *
 * Most calls resolve immediately (create-new/add-to-existing). Only symbol-mismatch
 * goes async via modal interaction: the hook stores pending confirmation state,
 * opens the dialog, and resolves the original promise when the user picks an action.
 */
export function usePreTradeSessionConfirmation() {
  const dispatch = useAppStateDispatch();
  const activeSessionId = useAppStateSelector(selectActiveSessionId);
  const { data: targetSessionScenarios } = useGetPreTradeDataQuery(
    { SessionID: activeSessionId ?? undefined },
    { skip: !activeSessionId }
  );

  const disclosure = useDisclosure();
  // Tracks an unresolved symbol-change decision while dialog is open.
  const [pendingConfirmation, setPendingConfirmation] = useState<PendingConfirmationState | null>(null);

  /**
   * Completes the pending confirmation request.
   * This is the only place that resolves the promise created in `resolveSessionID`
   * for the symbol-mismatch path.
   */
  const handleConformationDialogueAction = useCallback(
    (action: SessionAction) => {
      // Close dialog first
      disclosure.close();

      if (!pendingConfirmation) {
        return;
      }

      const { resolve, sessionId } = pendingConfirmation;

      if (action === 'cancel') {
        setPendingConfirmation(null);
        resolve({ sessionId: '', action: 'cancel' });
        return;
      }

      if (action === 'add-to-existing') {
        dispatch(setActiveSession({ sessionId }));
        setPendingConfirmation(null);
        resolve({ sessionId, action: 'add-to-existing' });
        return;
      }

      const newSessionId = uuid();
      dispatch(setActiveSession({ sessionId: newSessionId }));
      setPendingConfirmation(null);
      resolve({ sessionId: newSessionId, action: 'create-new' });
    },
    [disclosure, dispatch, pendingConfirmation]
  );

  /**
   * Resolves the session decision for a pre-trade submit.
   * - no active session: create a new session
   * - same symbol already in active session: reuse active session
   * - different symbol: require user confirmation via modal
   * - symbols unavailable: create a new session to avoid reusing the wrong one
   *
   * @param newSymbol The symbol for the new scenario
   * @returns Promise that resolves with the sessionId to use, or empty string if cancelled
   */
  const resolveSessionID = useCallback(
    async (newSymbol: string): Promise<SessionConfirmationResult> => {
      // No active session yet, so we always start a fresh one.
      if (!activeSessionId) {
        return { sessionId: uuid(), action: 'create-new' };
      }

      // Prefer RTK Query cache when present, and dedupe symbols for stable comparison/dialog copy.
      let targetSessionSymbols = targetSessionScenarios
        ? Array.from(new Set(targetSessionScenarios.map(scenario => scenario.Symbol)))
        : null;

      // Cache miss path: fetch definitive symbols before deciding reuse vs confirmation.
      if (!targetSessionSymbols) {
        try {
          const scenarios = await dispatch(
            preTradeApi.endpoints.getPreTradeData.initiate({ SessionID: activeSessionId }, { subscribe: false })
          ).unwrap();
          targetSessionSymbols = Array.from(new Set(scenarios.map(scenario => scenario.Symbol)));
        } catch {
          targetSessionSymbols = null;
        }
      }

      // If symbols are unavailable (or unexpectedly empty), create a new session defensively.
      if (!targetSessionSymbols || targetSessionSymbols.length === 0) {
        return { sessionId: uuid(), action: 'create-new' };
      }

      // Same symbol: safe to reuse active session without prompting.
      if (targetSessionSymbols.includes(newSymbol)) {
        dispatch(setActiveSession({ sessionId: activeSessionId }));
        return { sessionId: activeSessionId, action: 'add-to-existing' };
      }

      // Different symbol: park request in local state and wait for modal action.
      return new Promise(resolve => {
        setPendingConfirmation({
          sessionId: activeSessionId,
          currentSymbols: targetSessionSymbols,
          newSymbol,
          resolve,
        });
        disclosure.open();
      });
    },
    [activeSessionId, targetSessionScenarios, dispatch, disclosure]
  );

  const ConfirmationDialog = pendingConfirmation ? (
    <SessionConfirmationDialog
      isOpen={disclosure.isOpen}
      currentSymbol={pendingConfirmation.currentSymbols.join(', ')}
      newSymbol={pendingConfirmation.newSymbol}
      onAction={handleConformationDialogueAction}
    />
  ) : null;

  return { resolveSessionID, ConfirmationDialog };
}
